import React, { FC, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Button } from 'reactstrap';
import { FormLayout } from '../../../../shared/kiosk';
import { InlineHelp, MaterialIcon, RichMarkdownIt } from '../../../components';
import { ErrorBoundary } from '../../../components/ErrorBoundary';
import { useTranslation } from '../../../translations';
import { Fetcher, isNonEmpty, useFetcher } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import { EditStyleSheet } from './EditStyleSheet';
import { PageEditor } from './EditorDragContext';
import { RootFormState } from './FormElements';
import {
  ExpressEditorOptionContext,
  OptionContextProps,
  RegFormEditorConfig,
} from './OptionContext';

import './style.scss';

export const ExpressEditorComponent: FC = () => {
  const [config, setConfig] = useState<OptionContextProps>(() => ({
    config: RegFormEditorConfig,
    options: [],
  }));

  const fetch = useFetcher(async () => {
    // Get options from product + registration
    const options = await api.getOptions('product');
    setConfig((old) => ({ ...old, options }));

    const pageLayout = await api.getRegistrationFormLayout(true);

    const defaultLayout: FormLayout = {
      finalPage: {
        id: 'final',
        name: 'Confirmation',
        sections: [],
      },
      footer: '',
      header: '',
      disableBootstrap: false,
      pages: [pageLayout],
      paymentOptions: {
        showPaymentScreen: true,
        markdown: '',
      },
      stylesheet: '',
      title: 'Express Editor',
    };

    return { layout: defaultLayout };
  }, []);

  const { ts } = useTranslation();
  const save = useCallback(
    async (layout: FormLayout) => {
      if (!isNonEmpty(layout.pages)) {
        return;
      }

      try {
        await api.updateRegistrationFormLayout(layout.pages[0]);
        toast.success(ts('saved'));
      } catch (error) {
        captureError(error as Error);
      }
    },
    [ts],
  );

  if (!fetch.success) {
    return <Fetcher result={fetch} />;
  }

  return (
    <ExpressEditorComponentInner
      backLink="/housekeeping/settings"
      config={config}
      hideBeta
      initialLayout={fetch.data.layout}
      save={save}
    />
  );
};

interface ExpressEditorComponentInnerProps {
  readonly config: OptionContextProps;
  readonly initialLayout: FormLayout;
  readonly backLink: string;
  readonly hideBeta?: boolean;
  readonly save: (layout: FormLayout) => void;
}

export const ExpressEditorComponentInner: FC<ExpressEditorComponentInnerProps> = ({
  initialLayout,
  config,
  backLink,
  hideBeta,
  save,
}) => {
  const { ts } = useTranslation();
  const [state, setState] = useState<RootFormState>({
    toolboxTab: 'toolbox',
    layout: initialLayout,
    activePage: initialLayout.pages[0]?.id ?? null,
  });

  const [view, setView] = useState<'css' | 'pages'>('pages');
  const onClickSave = useCallback(() => {
    save(state.layout);
  }, [save, state.layout]);

  return (
    <ErrorBoundary redirectText={ts('back_to_housekeeping')} redirectTo="/housekeeping">
      <ExpressEditorOptionContext.Provider value={config}>
        <div className="express-editor">
          <header className="express-editor-header">
            <div className="header-back header-icon">
              <Link to={backLink}>
                <MaterialIcon name="arrow_back" style={{ color: 'white' }} />
              </Link>
            </div>
            <div>
              {ts('express_mode_editor')}
              {!hideBeta && (
                <>
                  &mdash; <Badge color="warning">{ts('beta_feature')}</Badge>
                  <span className="text-white">
                    <InlineHelp>
                      <strong>{ts('what_does_this_mean')}</strong>
                      <RichMarkdownIt
                        source={ts('the_express_mode_editor_is_still_in_beta_and_is')}
                      />
                    </InlineHelp>
                  </span>{' '}
                </>
              )}
            </div>
          </header>
          <header className="express-editor-subheader">
            <div className="page-name">
              {config.config.allowCss && (
                <Button
                  color="secondary"
                  onClick={() => {
                    setView(view === 'pages' ? 'css' : 'pages');
                  }}
                >
                  {view === 'pages' && ts('edit_custom_css')}
                  {view === 'css' && ts('back_to_pages')}
                </Button>
              )}
            </div>
            <div>
              <Button color="primary" id="saveForm" onClick={onClickSave}>
                {ts('save_changes')}
              </Button>
            </div>
          </header>
          {view === 'pages' && <PageEditor setState={setState} state={state} />}
          {view === 'css' && <EditStyleSheet setState={setState} state={state} />}
        </div>
      </ExpressEditorOptionContext.Provider>
    </ErrorBoundary>
  );
};
